import type { Extension } from "@codemirror/state";
import { latexLanguage } from "./latex-language";

type LanguageLoader = () => Promise<Extension>;

const languageLoaders: Record<string, LanguageLoader> = {
  javascript: () =>
    import("@codemirror/lang-javascript").then((m) => m.javascript()),
  typescript: () =>
    import("@codemirror/lang-javascript").then((m) =>
      m.javascript({ typescript: true })
    ),
  python: () => import("@codemirror/lang-python").then((m) => m.python()),
  html: () => import("@codemirror/lang-html").then((m) => m.html()),
  xml: () =>
    import("@codemirror/lang-html").then((m) => m.html({ matchClosingTags: true })),
  css: () => import("@codemirror/lang-css").then((m) => m.css()),
  json: () => import("@codemirror/lang-json").then((m) => m.json()),
  sql: () => import("@codemirror/lang-sql").then((m) => m.sql()),
  markdown: () =>
    import("@codemirror/lang-markdown").then((m) => m.markdown()),
};

// LaTeX is loaded synchronously (already bundled)
const syncLanguages: Record<string, Extension> = {
  latex: latexLanguage,
};

// Cache loaded extensions to avoid repeated imports
const loadedCache = new Map<string, Extension>();

export async function getLanguageExtension(
  language: string
): Promise<Extension | null> {
  const lang = language.toLowerCase();

  // Check sync languages first
  if (lang in syncLanguages) {
    return syncLanguages[lang];
  }

  // Check cache
  const cached = loadedCache.get(lang);
  if (cached) return cached;

  // Try lazy loader
  const loader = languageLoaders[lang];
  if (!loader) return null;

  const ext = await loader();
  loadedCache.set(lang, ext);
  return ext;
}
